import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import { Plus, Pencil, Trash2, Building2, X, Layers, ChevronDown, ChevronRight } from 'lucide-react';

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Binalar() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [kurumlar, setKurumlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);       // null | 'ekle' | bina_obj
  const [katModal, setKatModal] = useState(null); // null | bina_obj
  const [form, setForm] = useState({ ad: '', adres: '', kurum_id: '', aktif: true });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});   // bina_id -> bool

  /* Kat yönetimi state */
  const [katlar, setKatlar] = useState([]);        // seçili binanın katları
  const [katLoading, setKatLoading] = useState(false);
  const [yeniKat, setYeniKat] = useState('');
  const [editKat, setEditKat] = useState(null);    // {id, ad}

  const load = async () => {
    setLoading(true);
    try {
      const promises = [api.get('/binalar')];
      if (user.rol === 'superadmin') promises.push(api.get('/kurumlar'));
      const [b, k] = await Promise.all(promises);
      setList(b.data);
      if (k) setKurumlar(k.data);
    } catch { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  /* Kat listesi yükle */
  const loadKatlar = async (binaId) => {
    setKatLoading(true);
    try {
      const { data } = await api.get(`/binalar/${binaId}/katlar`);
      setKatlar(data);
    } catch { setKatlar([]); }
    finally { setKatLoading(false); }
  };

  const openKatModal = async (b) => {
    setKatModal(b);
    setYeniKat('');
    setEditKat(null);
    await loadKatlar(b.id);
  };

  const handleKatEkle = async () => {
    if (!yeniKat.trim()) return toast.error('Kat adı giriniz');
    try {
      await api.post(`/binalar/${katModal.id}/katlar`, { ad: yeniKat.trim() });
      setYeniKat('');
      await loadKatlar(katModal.id);
      toast.success('Kat eklendi');
    } catch { toast.error('Eklenemedi'); }
  };

  const handleKatGuncelle = async () => {
    if (!editKat.ad.trim()) return toast.error('Kat adı giriniz');
    try {
      await api.put(`/binalar/katlar/${editKat.id}`, { ad: editKat.ad.trim() });
      setEditKat(null);
      await loadKatlar(katModal.id);
      toast.success('Güncellendi');
    } catch { toast.error('Güncellenemedi'); }
  };

  const handleKatSil = async (katId) => {
    if (!window.confirm('Bu katı silmek istiyor musunuz? Bu kata bağlı lokasyonlar etkilenebilir.')) return;
    try {
      await api.delete(`/binalar/katlar/${katId}`);
      await loadKatlar(katModal.id);
      toast.success('Kat silindi');
    } catch { toast.error('Silinemedi'); }
  };

  /* Bina CRUD */
  const openEkle = () => {
    setForm({ ad: '', adres: '', kurum_id: user.rol === 'superadmin' ? '' : user.kurum_id, aktif: true });
    setModal('ekle');
  };

  const openDuzenle = b => {
    setForm({ ad: b.ad, adres: b.adres || '', kurum_id: b.kurum_id, aktif: b.aktif });
    setModal(b);
  };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal === 'ekle') { await api.post('/binalar', form); toast.success('Bina eklendi'); }
      else { await api.put(`/binalar/${modal.id}`, form); toast.success('Bina güncellendi'); }
      setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Hata'); }
    finally { setSaving(false); }
  };

  const handleDelete = async b => {
    if (!window.confirm(`"${b.ad}" binasını silmek istiyor musunuz?\nBuna bağlı lokasyonlar da etkilenebilir.`)) return;
    try { await api.delete(`/binalar/${b.id}`); toast.success('Silindi'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Silinemedi'); }
  };

  const filtered = list.filter(b => b.ad.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Binalar</h1>
          <p className="page-subtitle">{list.length} bina kayıtlı</p>
        </div>
        <button className="btn btn-primary" onClick={openEkle}><Plus size={16} /> Yeni Bina</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div className="search-box" style={{ maxWidth: 300 }}>
          <span className="search-icon">
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          </span>
          <input className="input" placeholder="Bina ara..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-blue" style={{ width: 32, height: 32 }} />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Bina Adı</th>
                  {user.rol === 'superadmin' && <th>Kurum</th>}
                  <th>Adres</th>
                  <th>Durum</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => (
                  <tr key={b.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Building2 size={16} color="var(--blue)" />
                        </div>
                        <span style={{ fontWeight: 600 }}>{b.ad}</span>
                      </div>
                    </td>
                    {user.rol === 'superadmin' && <td>{b.kurum_adi || '—'}</td>}
                    <td style={{ color: 'var(--gray-600)', fontSize: 13 }}>{b.adres || '—'}</td>
                    <td>
                      <span className={`badge ${b.aktif ? 'badge-green' : 'badge-gray'}`}>{b.aktif ? 'Aktif' : 'Pasif'}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => openKatModal(b)}
                          title="Kat / Servis Yönetimi"
                        >
                          <Layers size={13} /> Katlar
                        </button>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openDuzenle(b)}>
                          <Pencil size={14} />
                        </button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(b)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
                    {search ? 'Arama sonucu bulunamadı' : 'Henüz bina yok'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bina Ekle / Düzenle Modal */}
      {modal && (
        <Modal title={modal === 'ekle' ? 'Yeni Bina' : 'Binayı Düzenle'} onClose={() => setModal(null)}>
          <form onSubmit={handleSave}>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {user.rol === 'superadmin' && (
                <div className="input-group">
                  <label className="input-label">Kurum *</label>
                  <select className="input" required value={form.kurum_id} onChange={e => setForm({...form, kurum_id: e.target.value})}>
                    <option value="">Seçin...</option>
                    {kurumlar.map(k => <option key={k.id} value={k.id}>{k.ad}</option>)}
                  </select>
                </div>
              )}
              <div className="input-group">
                <label className="input-label">Bina Adı *</label>
                <input className="input" required value={form.ad} onChange={e => setForm({...form, ad: e.target.value})} placeholder="Merkez Ofis Binası" />
              </div>
              <div className="input-group">
                <label className="input-label">Adres</label>
                <textarea className="input" rows={2} value={form.adres} onChange={e => setForm({...form, adres: e.target.value})} placeholder="Tam adres..." style={{ resize: 'vertical' }} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.aktif} onChange={e => setForm({...form, aktif: e.target.checked})} />
                <span className="input-label" style={{ margin: 0 }}>Aktif</span>
              </label>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>İptal</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <div className="spinner" /> : null} Kaydet
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Kat Yönetimi Modal */}
      {katModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div>
                <span className="modal-title"><Layers size={15} style={{ marginRight: 6, verticalAlign: 'middle', color: 'var(--blue)' }} />{katModal.ad} — Kat / Servis Yönetimi</span>
                <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>Binadaki kat ve servis adlarını tanımlayın</div>
              </div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setKatModal(null)}><X size={16} /></button>
            </div>

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Kat listesi */}
              {katLoading ? (
                <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner spinner-blue" style={{ width: 22, height: 22, margin: '0 auto' }} /></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
                  {katlar.map(k => (
                    <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--gray-50)', borderRadius: 10, border: '1px solid var(--gray-100)' }}>
                      {editKat?.id === k.id ? (
                        <>
                          <input
                            className="input" style={{ flex: 1, padding: '6px 10px', fontSize: 13 }}
                            value={editKat.ad}
                            onChange={e => setEditKat({ ...editKat, ad: e.target.value })}
                            onKeyDown={e => e.key === 'Enter' && handleKatGuncelle()}
                            autoFocus
                          />
                          <button className="btn btn-primary btn-sm" onClick={handleKatGuncelle}>Kaydet</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditKat(null)}>İptal</button>
                        </>
                      ) : (
                        <>
                          <Layers size={13} color="var(--blue)" style={{ flexShrink: 0 }} />
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{k.ad}</span>
                          <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setEditKat({ id: k.id, ad: k.ad })}><Pencil size={12} /></button>
                          <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleKatSil(k.id)}><Trash2 size={12} /></button>
                        </>
                      )}
                    </div>
                  ))}
                  {!katlar.length && !katLoading && (
                    <p style={{ color: 'var(--gray-400)', fontSize: 13, textAlign: 'center', padding: '16px 0', margin: 0 }}>
                      Henüz kat tanımlanmamış
                    </p>
                  )}
                </div>
              )}

              {/* Yeni kat ekle */}
              <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 6 }}>
                  Yeni Kat / Servis Ekle
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="input" style={{ flex: 1 }}
                    placeholder="Örn: 2. Kat, Dahiliye Servisi, İdari Kat..."
                    value={yeniKat}
                    onChange={e => setYeniKat(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleKatEkle())}
                  />
                  <button className="btn btn-primary" onClick={handleKatEkle}>
                    <Plus size={15} /> Ekle
                  </button>
                </div>
                <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 6, marginBottom: 0 }}>
                  Lokasyon eklerken bu isimlere göre filtreleme yapılabilir.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setKatModal(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}